import { useState } from 'react';
import { usePaymentApi } from '../paymentApiContext';
import logger from '../../../../utils/logging/logger';
import { formatErrorMessage } from './helpers';

// Promo-code and bulk-pricing-removal handlers for usePaymentForm, extracted
// VERBATIM. Bulk pricing itself (state + the auto-calculation on load) is
// owned by useCourseProductFetch; this hook only mutates it (clearing it when
// a promo is applied, recalculating it when a promo is removed) via the
// setBulkPricing passed in.
export function usePromoCode({
  courses,
  productIds,
  courseId,
  courseIds,
  bulkPricing,
  setBulkPricing,
  setBulkPricingLoading,
  appliedPromo,
  setAppliedPromo,
  prevBulkPricingRef,
  apiCallTimestampsRef,
  abortControllerRef,
  RATE_LIMIT_DELAY,
  scrollToPaymentTop,
}) {
  const paymentApi = usePaymentApi(); // signed-in api, or the guest adapter for new students
  const [promoError, setPromoError] = useState(null);
  const [promoInfo, setPromoInfo] = useState(null); // FIX: Info message for promo code application (e.g., when replacing bulk pricing)
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false); // FIX #13: Loading state for promo code

  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;

    // FIX #13: Add loading state for promo code application
    if (promoLoading) return; // Prevent multiple simultaneous requests

    // FIX: Check if bulk pricing is active first - prevent promo code application if it is
    // This saves processing by not making the API call if bulk pricing is already applied
    if (bulkPricing && bulkPricing.pricing && bulkPricing.pricing.savings > 0) {
      setPromoInfo(null); // Clear any info messages
      setPromoError('Promo code cannot be combined with existing discounts. Please remove the bulk discount.');
      logger.info('Promo code application blocked - bulk pricing is active', {
        promoCode: promoCode.trim().toUpperCase(),
        bulkPricingDiscount: bulkPricing.pricing.discountAmount
      });
      return; // Exit early - don't make API call
    }

    // FIX #31: Rate limiting - prevent rapid API calls
    const now = Date.now();
    const lastCallTime = apiCallTimestampsRef.current.applyPromoCode;
    if (lastCallTime && (now - lastCallTime) < RATE_LIMIT_DELAY) {
      logger.debug('Promo code application rate limited, skipping');
      return;
    }
    apiCallTimestampsRef.current.applyPromoCode = now;

    // Clear previous promo error (but keep success message if promo is already applied)
    setPromoError(null);
    // Only clear info message if applying a new code (not if one is already applied)
    if (!appliedPromo) {
      setPromoInfo(null);
    }
    setPromoLoading(true);

    try {
      // Ensure course IDs are integers (backend expects integers)
      // FIX: Add null safety checks for courses array
      const courseIds = productIds ? [] : (courses && Array.isArray(courses) ? courses.map(course => course && course.id ? parseInt(course.id) : null).filter(id => id !== null) : []);
      const productIdsArray = productIds ? (courses && Array.isArray(courses) ? courses.map(course => course && course.id ? parseInt(course.id) : null).filter(id => id !== null) : []) : [];

      logger.debug('Applying promo code', {
        code: promoCode.trim().toUpperCase(),
        productIds,
        courseIds,
        productIdsArray,
        courses: courses.map(c => ({ id: c.id, name: c.name }))
      });

      const response = await paymentApi.validatePromoCode(promoCode.trim().toUpperCase(), courseIds, productIdsArray);

      logger.debug('Promo code validation response', { response });

      if (response.success) {
        // Clear bulk pricing when promo code is applied (mutually exclusive)
        // Note: We already checked for bulk pricing above, but clear it here as a safety measure
        setBulkPricing(null);
        logger.info('Promo code applied, bulk pricing cleared');

        // FIX: Set appliedPromo state first, then let the discount change useEffect handle payment intent recreation
        // This ensures the state is updated before the payment intent is recreated
        setAppliedPromo({
          code: response.promoCode.code,
          discount_type: response.promoCode.discount_type,
          discount_value: response.promoCode.discount_value,
          description: response.promoCode.description,
          success_message: response.promoCode.success_message
        });
        setPromoCode('');
        // Display success message only if it's configured and not empty
        // Check for null, undefined, and empty strings
        const successMessage = response.promoCode.success_message;
        if (successMessage && successMessage.trim().length > 0) {
          setPromoInfo(successMessage.trim());
        } else {
          setPromoInfo(null);
        }

        // FIX #7: The discount change useEffect will automatically detect the promo code change
        // and recreate the payment intent with the correct discounted amount
        // No need to manually trigger here - the useEffect will handle it
        logger.debug('Promo code applied, discount change useEffect will recreate payment intent', {
          promoCode: response.promoCode.code,
          discountType: response.promoCode.discount_type,
          discountValue: response.promoCode.discount_value
        });
        requestAnimationFrame(scrollToPaymentTop);
      } else {
        setPromoError(formatErrorMessage(response.message || 'Invalid promo code'));
        setPromoInfo(null); // Clear info message on error
        requestAnimationFrame(scrollToPaymentTop);
      }
    } catch (error) {
      logger.error('Error applying promo code', { error, code: promoCode, errorMessage: error.message, errorResponse: error.response });
      // FIX #11 & #20: Distinguish between network errors, timeout errors, and server errors
      const isTimeoutError = error.isTimeout || error.message?.includes('timeout') || error.name === 'AbortError';
      const isNetworkError = !error.response && (error.message?.includes('fetch') || error.message?.includes('network'));
      const errorMessage = isTimeoutError
        ? 'Request timed out. Please check your internet connection and try again.'
        : isNetworkError
        ? 'Network error. Please check your connection and try again.'
        : (error.response?.data?.message || error.message || 'Failed to apply promo code. Please try again.');
      setPromoError(formatErrorMessage(errorMessage));
    } finally {
      // FIX #13: Clear loading state
      setPromoLoading(false);
    }
  };

  // Remove bulk pricing discount
  const removeBulkPricing = async () => {
    // Only remove if bulk pricing is actually applied
    if (!bulkPricing) {
      return; // No bulk pricing to remove
    }

    // FIX #31: Rate limiting - prevent rapid API calls
    const now = Date.now();
    // Its own rate-limit slot: sharing removePromoCode's made a promo removal right after a bulk
    // removal (a normal sequence) silently do nothing.
    const lastCallTime = apiCallTimestampsRef.current.removeBulkPricing;
    if (lastCallTime && (now - lastCallTime) < RATE_LIMIT_DELAY) {
      logger.debug('Bulk pricing removal rate limited, skipping');
      return;
    }
    apiCallTimestampsRef.current.removeBulkPricing = now;

    // Clear bulk pricing state
    prevBulkPricingRef.current = null;
    setBulkPricing(null);

    // Clear from sessionStorage
    if (typeof sessionStorage !== 'undefined') {
      const paramsKey = `${courseId || ''}_${courseIds || ''}_${productIds || ''}`;
      const bulkPricingKey = `paymentForm_bulkPricing_${paramsKey}`;
      sessionStorage.removeItem(bulkPricingKey);
    }

    logger.info('Bulk pricing removed');
    // The discount change useEffect will automatically detect the bulk pricing change
    // and recreate the payment intent with the correct amount (without bulk discount)
  };

  // FIX #1 & #5: Make removePromoCode async with proper error handling
  // FIX #37: Already async - verified complete implementation
  const removePromoCode = async () => {
    // FIX #18: Add validation - only recalculate if promo was actually applied
    if (!appliedPromo) {
      return; // No promo to remove
    }

    // FIX #31: Rate limiting - prevent rapid API calls
    const now = Date.now();
    const lastCallTime = apiCallTimestampsRef.current.removePromoCode;
    if (lastCallTime && (now - lastCallTime) < RATE_LIMIT_DELAY) {
      logger.debug('Promo code removal rate limited, skipping');
      return;
    }
    apiCallTimestampsRef.current.removePromoCode = now;

    setAppliedPromo(null);
    setPromoError(null); // Clear any promo errors
    setPromoInfo(null); // Clear success message when promo code is removed

    // Recalculate bulk pricing when promo code is removed (if applicable)
    if (courses && Array.isArray(courses) && courses.length > 1 && !productIds) {
      setBulkPricingLoading(true); // FIX #14: Set loading state
      try {
        // FIX #13: Check if component is still mounted
        if (abortControllerRef.current?.signal.aborted) {
          logger.debug('Bulk pricing recalculation cancelled - component unmounted');
          return;
        }

        const courseIdsForBulk = courses.filter(course => course && course.id).map(course => parseInt(course.id));
        const bulkResponse = await paymentApi.calculateBulkPricing(courseIdsForBulk);

        // FIX #13: Check again if component is still mounted before setting state
        if (abortControllerRef.current?.signal.aborted) {
          logger.debug('Bulk pricing response received but component unmounted - ignoring');
          return;
        }

        if (bulkResponse.success && bulkResponse.bulkPricing) {
          // CRITICAL: Not restoring - this is a fresh calculation, so update prevRef
          prevBulkPricingRef.current = bulkResponse;
          setBulkPricing(bulkResponse);
          // Store bulk pricing in sessionStorage for future remounts
          if (typeof sessionStorage !== 'undefined') {
            const paramsKey = `${courseId || ''}_${courseIds || ''}_${productIds || ''}`;
            const bulkPricingKey = `paymentForm_bulkPricing_${paramsKey}`;
            sessionStorage.setItem(bulkPricingKey, JSON.stringify(bulkResponse));
          }
          logger.info('Bulk pricing recalculated after promo code removal');
        } else {
          setBulkPricing(null);
          logger.debug('No bulk pricing available after promo code removal');
        }
      } catch (error) {
        // FIX #5: Proper error handling - log and clear bulk pricing
        // FIX #13: Don't set state if component is unmounted
        if (abortControllerRef.current?.signal.aborted) {
          logger.debug('Error occurred but component unmounted - ignoring');
          return;
        }
        logger.error('Error recalculating bulk pricing after promo removal', { error });
        setBulkPricing(null);
        // Don't show error to user - bulk pricing is optional
      } finally {
        // FIX #14: Clear loading state (only if component is still mounted)
        if (!abortControllerRef.current?.signal.aborted) {
          setBulkPricingLoading(false);
        }
      }
    } else {
      // Clear bulk pricing if not applicable
      setBulkPricing(null);
      setBulkPricingLoading(false);
    }
  };

  return {
    applyPromoCode,
    promoCode,
    promoError,
    promoInfo,
    promoLoading,
    removeBulkPricing,
    removePromoCode,
    setPromoCode,
    setPromoError,
    setPromoInfo,
  };
}
